// The web store keeps every save block in the IndexedDB of the browser (MWSCache) and uploads them
// one by one, oldest first. When the server rejects one, it stays first in the queue and the ones
// behind it never go up. This helper tracks which one failed (and what the server answered) so the
// pending list can show it and let the user delete it, and the rest are sent again.

let SaveQueueDidChange = "SaveQueueDidChange";

class SaveQueueItem
{
    index:number = null;
    transaction:string = null;
    date:Date = null;
    entities:any[] = [];
    failed = false;
    errorCode:number = null;
    errorMessage:string = null;
}

class SaveQueueHelper extends MIOObject
{
    private static _sharedInstance:SaveQueueHelper = null;
    static sharedInstance() : SaveQueueHelper {
        if (this._sharedInstance == null) {
            this._sharedInstance = new SaveQueueHelper();
            this._sharedInstance.init();
        }
        return this._sharedInstance;
    }

    failedTransaction:string = null;
    pendingCount = 0;

    // The last answer of the server for each transaction that failed
    private errors:{[transaction:string]:{code:number, data:any}} = {};

    init(){
        super.init();

        let nc = MIONotificationCenter.defaultCenter();

        nc.addObserver(this, "MWSRequestFetchError", function(this:SaveQueueHelper, note:MIONotification){
            let request = note.object as MWSRequest;
            if (request == null || request.type != MWSRequestType.Save) return;
            this.errors[request.transaction] = { "code": request.resultCode, "data": request.resultData };
        });

        nc.addObserver(this, "MWSPersistentStoreSaveOperationError", function(this:SaveQueueHelper, note:MIONotification){
            this.failedTransaction = note.object;
            this.refresh();
        });

        nc.addObserver(this, "MWSRequestReceivedFetch", function(this:SaveQueueHelper, note:MIONotification){
            let request = note.object as MWSRequest;
            if (request == null || request.type != MWSRequestType.Save) return;
            if (request.resultCode < 200 || request.resultCode >= 300) return;

            delete this.errors[request.transaction];
            if (request.transaction == this.failedTransaction) this.failedTransaction = null;
            // The store deletes the block after this notification, so count once it is done
            setTimeout(() => { this.refresh(); }, 500);
        });
    }

    private get schema() : string {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        return ad.webService.identifier;
    }

    // Counts the pending blocks and tells the observers (the topbar) about it
    refresh(){
        this.fetchPendingItems((items:SaveQueueItem[]) => {
            this.pendingCount = items.length;
            if (items.length == 0) this.failedTransaction = null;
            MIONotificationCenter.defaultCenter().postNotification(SaveQueueDidChange, this);
        });
    }

    fetchPendingItems(completion:(items:SaveQueueItem[]) => void){
        let schema = this.schema;
        if (schema == null) { completion([]); return; }

        MWSCache.sharedInstance().fetch(schema, (blocks:any[]) => {
            let items = (blocks ?? []).map((block:any) => this.itemFromBlock(block));
            completion(items);
        });
    }

    private itemFromBlock(block:any) : SaveQueueItem {
        let item = new SaveQueueItem();
        item.index = block["index"];
        item.transaction = block["transaction"];

        // The url of the block ends with "?ts=<milliseconds>" (see WebService.saveRequestForWebStore)
        let url = block["url"];
        if (typeof url != "string") url = url?.absoluteString;
        let match = /ts=(\d+)/.exec(url ?? "");
        if (match != null) item.date = new Date(parseInt(match[1]));

        let body = block["body"];
        if (typeof body == "string") {
            try { body = JSON.parse(body); } catch (e) { body = null; }
        }
        item.entities = body?.["entities"] ?? [];

        item.failed = (item.transaction == this.failedTransaction);
        let error = this.errors[item.transaction];
        if (error != null) {
            item.errorCode = error.code;
            item.errorMessage = SaveQueueHelper.messageFromResponse(error.data);
        }

        return item;
    }

    static messageFromResponse(data:any) : string {
        if (data == null) return null;

        let value = data;
        if (typeof value == "string") {
            try { value = JSON.parse(value); } catch (e) { /* plain text */ }
        }
        if (typeof value == "object") value = value["error"] ?? value["message"] ?? value["reason"] ?? JSON.stringify(value);

        let message = String(value);
        return message.length > 300 ? message.substring(0, 300) + "…" : message;
    }

    // Sends the queue again, starting with the first block
    retry(){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        this.failedTransaction = null;
        ad.webPersistentStore.checkTransactions(this.schema, null);
        MIONotificationCenter.defaultCenter().postNotification(SaveQueueDidChange, this);
    }

    // Drops the block from the queue (its changes never reach the server) and sends the rest
    deleteItem(item:SaveQueueItem, completion:() => void){
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        delete this.errors[item.transaction];
        if (item.transaction == this.failedTransaction) this.failedTransaction = null;

        ad.webPersistentStore.deleteTransaction(this.schema, item.transaction, () => {
            this.refresh();
            if (completion != null) completion();
        });
    }
}
